import React, { useEffect, useState } from "react";
import { AlertCircle, Heart, Users, Briefcase, Sparkles, Activity, User, ChevronDown, ChevronUp, X } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, Cell, Tooltip } from "recharts";
import { format } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../../context/LanguageContext";

import { CONFIG } from "../../config";
const API_BASE = CONFIG.API_URL;

const MOOD_SCORES = {
    "Anxious": 2, "Lonely": 3, "Overwhelmed": 1, "Hopeful": 5, "Just chatting": 4, "Confused": 3, "Happy": 5, "Sad": 1, "Neutral": 3
};
const COLORS = {
    "Anxious": "#f87171", "Lonely": "#fbbf24", "Overwhelmed": "#ef4444", "Hopeful": "#34d399", "Just chatting": "#60a5fa", "Confused": "#a78bfa", "Happy": "#10b981", "Sad": "#6b7280", "Neutral": "#9ca3af"
};

export default function ContextCard({ user, currentUser }) {
    const { t } = useLanguage();
    // 🛡️ SAFETY CHECK: Don't show context card for self or inanimate users
    if (!user?.seeker?.categories) return null;
    const currentUserId = Number(currentUser?.id || currentUser?.userId);
    const targetUserId = Number(user?.id || user?.userId);
    
    if (currentUserId === targetUserId) return null;

    const [isExpanded, setIsExpanded] = useState(false);
    const [isVisible, setIsVisible] = useState(true);

    const categories = user.seeker.categories.map(sc => sc.category);
    const seekerInfo = user.seeker;

    // 1. Define Hierarchy
    const LAYER_CONFIG = {
        HIGH_RISK: { score: 10, color: "bg-indigo-50/50 text-indigo-700 border-indigo-100 dark:bg-indigo-900/20 dark:text-indigo-300 dark:border-indigo-800", icon: AlertCircle, labelKey: "ctx_critical" },
        EMOTIONAL_SUPPORT: { score: 8, color: "bg-purple-50/50 text-purple-700 border-purple-100 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-800", icon: Heart, labelKey: "ctx_emotional" },
        RELATIONSHIPS: { score: 6, color: "bg-pink-50/50 text-pink-700 border-pink-100 dark:bg-pink-900/20 dark:text-pink-300 dark:border-pink-800", icon: Users, labelKey: "ctx_rel" },
        LIFE_CHALLENGES: { score: 4, color: "bg-blue-50/50 text-blue-700 border-blue-100 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800", icon: Briefcase, labelKey: "ctx_life" },
        SPIRITUAL: { score: 2, color: "bg-teal-50/50 text-teal-700 border-teal-100 dark:bg-teal-900/20 dark:text-teal-300 dark:border-teal-800", icon: Sparkles, labelKey: "ctx_spirit" }
    };

    // 2. Logic: Mutual vs General
    const providerCats = currentUser?.provider?.categories || [];
    const providerCatIds = new Set(providerCats.map(pc => pc.categoryId));

    const mutual = categories.filter(c => providerCatIds.has(c.id));
    const hasMutual = mutual.length > 0;

    let displayList = hasMutual ? mutual : categories;

    // Additional Sort by Score (High Risk first)
    displayList = [...displayList].sort((a, b) => {
        const scoreA = LAYER_CONFIG[a.layer]?.score || 0;
        const scoreB = LAYER_CONFIG[b.layer]?.score || 0;
        return scoreB - scoreA;
    });

    if (displayList.length === 0 || !isVisible) return null;

    const primary = displayList[0];
    const config = LAYER_CONFIG[primary.layer] || LAYER_CONFIG.EMOTIONAL_SUPPORT;
    const Icon = config.icon;
    const extraCount = displayList.length - 1;


    return (
        <div className="flex flex-col gap-2 px-4 py-2">
            {/* Primary Context Banner (Interactable) */}
            <motion.div
                layout
                className={`rounded-xl border shadow-sm overflow-hidden ${config.color} relative`}
            >
                <div
                    className="p-3 flex items-center justify-between cursor-pointer hover:bg-white/30 transition-colors"
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-full bg-white/60 backdrop-blur-sm shadow-sm">
                            <Icon size={18} />
                        </div>
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-wider opacity-70 mb-0.5">
                                {hasMutual ? t('ctx_mutual') : t('ctx_primary')}
                            </p>
                            <div className="flex items-center gap-2">
                                <span className="font-bold text-sm">{t(primary.titleKey || primary.label)}</span>
                                {extraCount > 0 && !isExpanded && (
                                    <span className="text-[10px] px-2 py-0.5 bg-white/50 rounded-full font-medium">
                                        {t('ctx_more', { count: extraCount })}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {/* Dismiss Button */}
                        <button
                            onClick={(e) => { e.stopPropagation(); setIsVisible(false); }}
                            className="p-1 hover:bg-black/10 rounded-full transition-colors"
                            title={t('ctx_dismiss')}
                        >
                            <X size={14} />
                        </button>
                        {/* Expand Chevron */}
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </div>
                </div>

                {/* Expanded Details */}
                <AnimatePresence>
                    {isExpanded && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="px-4 pb-3 pt-0 border-t border-black/5"
                        >
                            <div className="mt-2 space-y-2">
                                {/* Extra Categories */}
                                {displayList.slice(1).map(cat => (
                                    <div key={cat.id} className="flex items-center gap-2 text-xs font-medium opacity-80">
                                        <span className="w-1.5 h-1.5 rounded-full bg-current opacity-50" />
                                        {t(cat.titleKey || cat.label)}
                                    </div>
                                ))}

                                {/* Seeker Info */}
                                {(user.name || seekerInfo?.age) && (
                                    <div className="flex items-center gap-3 mt-3 pt-2 border-t border-black/5">
                                        <div className="flex items-center gap-1.5 opacity-80">
                                            <User size={14} />
                                            <span className="text-xs font-semibold">{user.name}</span>
                                        </div>
                                        {seekerInfo?.age && <div className="text-[10px] opacity-60">{t('ctx_age')}: {seekerInfo.age}</div>}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>

        </div>
    );
}
